/*
  Ever Leaf Hero — Background Slideshow controller.

  A passive, decorative background crossfade for the shared ever-leaf-hero
  section. Server-rendered markup (sections/ever-leaf-hero.liquid) already
  produces every valid slide as a <picture class="ever-leaf-hero__slide">,
  with the first one carrying --active (opacity: 1) — so the correct first
  paint exists with zero JS dependency; this file only adds the recurring
  crossfade on top of that baseline. If this script never loads, or a
  visitor prefers reduced motion, the first slide simply stays static.

  Timing model (see the section's Slide/Fade duration settings):
  "Slide duration" is the total interval from one slide becoming fully
  active to the next becoming fully active. "Fade duration" is the tail
  portion of that interval during which the crossfade happens, so the
  fade completes exactly at each slide-duration boundary. Concretely, with
  slide=7s/fade=1.4s: hold 0-5.6s, crossfade 5.6-7.0s, repeat. Implemented
  as one setTimeout for the first (shorter) hold, then a plain setInterval
  at the full slide-duration period from then on -- each interval tick
  simply flips the --active class, and the CSS `transition: opacity` (see
  section-ever-leaf-hero.css) does the actual crossfade animation for both
  the outgoing and incoming slide simultaneously.

  Per-slide light text: each slide element carries data-use-light-text
  (server-rendered from the Metaobject's use_light_text field). The same
  advance() call that flips --active also toggles ever-leaf-hero--light-text
  on the ancestor .ever-leaf-hero section based on the INCOMING slide's
  value -- one state change, same tick as the image crossfade, no separate
  timer. That class only ever changes `color` (see the CSS) -- text opacity
  is never touched here.

  Multiple Hero instances are safe: all state lives on the element
  instance, nothing module-level/global. Guarded connectedCallback +
  disconnectedCallback (matching the pattern already used by
  EverLeafGalleryApp/Testimonials) keeps Theme Editor section reloads from
  ever accumulating duplicate timers or listeners.
*/
(function () {
  'use strict';

  if (customElements.get('ever-leaf-hero-slideshow')) return;

  class EverLeafHeroSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.slides = Array.from(this.querySelectorAll('.ever-leaf-hero__slide'));
      this.heroSection = this.closest('.ever-leaf-hero');
      if (this.slides.length < 2) return; // 0 or 1 valid slide: static, no timer needed.

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const slideMs = parseInt(this.dataset.slideDurationMs, 10) || 7000;
      const fadeMs = parseInt(this.dataset.fadeDurationMs, 10) || 1400;
      // Clamp defensively: a merchant-configured fade >= slide duration must
      // never produce a negative delay -- degrade to "hold time 0" instead.
      const holdMs = Math.max(0, slideMs - fadeMs);

      this.activeIndex = this.slides.findIndex((slide) =>
        slide.classList.contains('ever-leaf-hero__slide--active')
      );
      if (this.activeIndex === -1) this.activeIndex = 0;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.advance = () => {
        const next = (this.activeIndex + 1) % this.slides.length;
        this.slides[this.activeIndex].classList.remove('ever-leaf-hero__slide--active');
        this.slides[next].classList.add('ever-leaf-hero__slide--active');
        this.activeIndex = next;

        if (this.heroSection) {
          const useLightText = this.slides[next].dataset.useLightText === 'true';
          this.heroSection.classList.toggle('ever-leaf-hero--light-text', useLightText);
        }
      };

      this.startCycle = () => {
        this.stopCycle();
        this.initialTimer = setTimeout(() => {
          this.advance();
          this.intervalTimer = setInterval(this.advance, slideMs);
        }, holdMs);
      };

      this.stopCycle = () => {
        if (this.initialTimer) {
          clearTimeout(this.initialTimer);
          this.initialTimer = null;
        }
        if (this.intervalTimer) {
          clearInterval(this.intervalTimer);
          this.intervalTimer = null;
        }
      };

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.stopCycle();
          } else {
            // Resume with a fresh hold-then-fade cycle rather than trying to
            // preserve exact elapsed sub-second timing -- simpler, no drift,
            // and explicitly never "catches up" through missed slides.
            this.startCycle();
          }
        },
        { signal }
      );

      if (!document.hidden) this.startCycle();
    }

    disconnectedCallback() {
      if (this.stopCycle) this.stopCycle();
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.initialized = false;
    }
  }

  customElements.define('ever-leaf-hero-slideshow', EverLeafHeroSlideshow);
})();
